import { useCallback, useState } from "react";

import { useAssetUrl } from "@skilitics/assets";
import { destructiveActionFormatStrings } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import { removeStickyStateByPrefix } from "@skilitics/nautilus-core-designer";
import type { InteractModuleListItem } from "@skilitics/nautilus-endpoint-definition";

import { useDuplicateModuleSettings } from "../../wizards/index.ts";
import { useDuplicateModule, useRemoveModule, useUpdateModule } from "../modules/modulePersistence.ts";
import type { MenuSelection } from "./ModuleMenu.tsx";

export type Status = "opening" | "locking" | "deleting" | "idle";

// todo: update placeholder image when available
export const placeholderThumbnailUrl = "/assets/placeholderModule.webp";

export const useModuleUrl = (moduleId: InteractModuleListItem["id"]) => `/modules/${moduleId}`;

export const useModuleItem = (module: InteractModuleListItem) => {
  const formatMessage = useFormatMessage(mergedFormatStrings);

  const moduleUrl = useModuleUrl(module.id);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);

  const [busy, setBusy] = useState<Status>("idle");
  const [hovered, setHovered] = useState(false);

  const url = useAssetUrl(module.thumbnailAssetId ?? "") ?? placeholderThumbnailUrl;

  const handleUpdated = useCallback(() => setBusy("idle"), []);
  const { updateModule } = useUpdateModule({ onSuccess: handleUpdated, onError: handleUpdated });

  const handleRemoved = useCallback(() => setBusy("idle"), []);
  const { removeModule } = useRemoveModule({ onSuccess: handleRemoved, onError: handleRemoved });

  const handleMenuOpen = useCallback(() => setHovered(true), []);

  // note: wait for the pop-up menu silk-screen to transition to prevent hover issues
  const handleMenuClose = useCallback(() => setTimeout(() => setHovered(false), 250), []);

  const handleRemove = useCallback(
    (removeConfirmed: boolean) => {
      setDeleteOpen(false);

      if (!removeConfirmed) return;

      setBusy("deleting");

      removeStickyStateByPrefix(module.id);
      removeModule(module.id);
    },
    [module.id, removeModule],
  );

  const { title, setTitle, reset } = useDuplicateModuleSettings();

  const handleCloseWizard = useCallback(() => {
    setDuplicateOpen(false);
    reset();
  }, [reset]);

  const { duplicateModuleAsync, status } = useDuplicateModule(module.id);

  const handleMenuSelection = useCallback<MenuSelection>(
    (key) => {
      switch (key) {
        case "lock":
        case "unlock": {
          updateModule({ ...module, readonly: !module.readonly });
          setBusy("locking");
          break;
        }

        case "delete": {
          setDeleteOpen(true);
          break;
        }

        case "duplicate": {
          setTitle(`${module.title}_(${formatMessage("duplicated")})`);
          setDuplicateOpen(true);
          break;
        }

        default:
          break;
      }

      handleMenuClose();
    },
    [formatMessage, handleMenuClose, module, setTitle, updateModule],
  );

  const handleCompleteWizard = useCallback(async () => {
    await duplicateModuleAsync({ ...module, title });
    // don't close the wizard if it fails
    handleCloseWizard();
  }, [duplicateModuleAsync, handleCloseWizard, module, title]);

  const alreadyOpenMessage = !module.alreadyOpenUser
    ? ""
    : module.alreadyOpenUser.isYou
      ? formatMessage("moduleAlreadyOpenByYou")
      : formatMessage("moduleAlreadyOpenByUser", module.alreadyOpenUser);

  return {
    formatMessage,
    status,
    moduleUrl,
    url,
    busy,
    setBusy,
    hovered,
    setHovered,
    alreadyOpenMessage,
    handleMenuOpen,
    handleMenuClose,
    handleMenuSelection,
    deleteDialogProps: {
      open: deleteOpen,
      header: formatMessage("header"),
      message: formatMessage("message", { title: module.title }),
      confirmLabel: formatMessage("confirmLabel"),
      cancelLabel: formatMessage("cancelLabel"),
      onConfirm: handleRemove,
    },
    duplicateWizardProps: {
      busy: status === "pending",
      open: duplicateOpen,
      onCancel: handleCloseWizard,
      onClose: handleCloseWizard,
      onComplete: handleCompleteWizard,
    },
  };
};

const formatStrings = {
  header: {
    en: "Delete Module?",
  },
  message: {
    en: 'You have requested the module "{title}" be deleted.\n\nAre you sure you wish to delete this module?',
  },
  duplicated: {
    en: "duplicated",
  },
  thumbnail: {
    en: "Module Thumbnail",
  },
  moduleAlreadyOpenByYou: {
    en: "You have this module open in another tab",
  },
  moduleAlreadyOpenByUser: {
    en: "This module is already open by {given_name} {family_name}",
  },
} satisfies FormatMessages;

const mergedFormatStrings = { ...formatStrings, ...destructiveActionFormatStrings };
